// Chemin: C:\gestion-copro\src\services\messageCS.service.ts
// Service CRUD pour la collection messagesCS - Communications opérationnelles via Gmail
// Conforme à SPECIFICATION_SENTINELLE_COMMUNICATIONS_SORTANTES_V2.md
// Session 53 : Correction bug Firestore "undefined" dans les votes

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  MessageCS,
  CreateMessageCSData,
  UpdateMessageCSData,
  StatutMessageCS,
  VoteMembreCS,
  RelanceManuelleCS,
  MessageCSEnValidation,
  DELAIS_RAPPELS,
} from '../types/messageCS.types';

// ============================================================================
// CONSTANTES
// ============================================================================

const getCollectionRef = (coproprieteId: string) =>
  collection(db, 'coproprietes', coproprieteId, 'messagesCS');

const getDocRef = (coproprieteId: string, messageId: string) =>
  doc(db, 'coproprietes', coproprieteId, 'messagesCS', messageId);

// ============================================================================
// CRUD DE BASE
// ============================================================================

/**
 * Créer un nouveau message CS
 */
export const createMessageCS = async (
  data: CreateMessageCSData
): Promise<string> => {
  const collectionRef = getCollectionRef(data.coproprieteId);
  
  const messageData = {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  const docRef = await addDoc(collectionRef, messageData);
  return docRef.id;
};

/**
 * Récupérer un message par son ID
 */
export const getMessageCSById = async (
  coproprieteId: string,
  messageId: string
): Promise<MessageCS | null> => {
  const docRef = getDocRef(coproprieteId, messageId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as MessageCS;
};

/**
 * Récupérer tous les messages d'une copropriété
 */
export const getAllMessagesCS = async (
  coproprieteId: string
): Promise<MessageCS[]> => {
  const collectionRef = getCollectionRef(coproprieteId);
  const q = query(collectionRef, orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as MessageCS[];
};

/**
 * Mettre à jour un message
 */
export const updateMessageCS = async (
  coproprieteId: string,
  messageId: string,
  data: UpdateMessageCSData
): Promise<void> => {
  const docRef = getDocRef(coproprieteId, messageId);
  
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
};

/**
 * Supprimer un message (brouillon uniquement)
 */
export const deleteMessageCS = async (
  coproprieteId: string,
  messageId: string
): Promise<void> => {
  // Vérifier que c'est un brouillon avant suppression
  const message = await getMessageCSById(coproprieteId, messageId);
  if (message && message.statut !== 'brouillon') {
    throw new Error('Seuls les brouillons peuvent être supprimés');
  }

  const docRef = getDocRef(coproprieteId, messageId);
  await deleteDoc(docRef);
};

// ============================================================================
// REQUÊTES PAR STATUT
// ============================================================================

/**
 * Récupérer les messages par statut
 */
export const getMessagesByStatut = async (
  coproprieteId: string,
  statut: StatutMessageCS
): Promise<MessageCS[]> => {
  const collectionRef = getCollectionRef(coproprieteId);
  const q = query(
    collectionRef,
    where('statut', '==', statut),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as MessageCS[];
};

/**
 * Récupérer les messages envoyés (pour onglet "Envoyés")
 */
export const getMessagesEnvoyes = async (
  coproprieteId: string,
  maxResults: number = 50
): Promise<MessageCS[]> => {
  const collectionRef = getCollectionRef(coproprieteId);
  const q = query(
    collectionRef,
    where('statut', '==', 'envoye'),
    orderBy('dateEnvoi', 'desc'),
    limit(maxResults)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as MessageCS[];
};

/**
 * Récupérer les messages en cours (brouillons + en_validation)
 */
export const getMessagesEnCours = async (
  coproprieteId: string
): Promise<MessageCS[]> => {
  const collectionRef = getCollectionRef(coproprieteId);
  const q = query(
    collectionRef,
    where('statut', 'in', ['brouillon', 'en_validation']),
    orderBy('updatedAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as MessageCS[];
};

// ============================================================================
// GESTION DES VOTES CS
// ============================================================================

/**
 * Soumettre un message au vote du Conseil Syndical
 */
export const soumettreAuVoteCS = async (
  coproprieteId: string,
  messageId: string,
  votesRequis: number
): Promise<void> => {
  const docRef = getDocRef(coproprieteId, messageId);

  await updateDoc(docRef, {
    statut: 'en_validation',
    validationCS: {
      dateSoumission: Timestamp.now(),
      votesRequis,
      votes: [],
      rappelsAuto: {},
      relancesManuelles: [],
    },
    updatedAt: Timestamp.now(),
  });
};

/**
 * Enregistrer le vote d'un membre CS
 */
export const enregistrerVote = async (
  coproprieteId: string,
  messageId: string,
  vote: VoteMembreCS
): Promise<{ nouveauStatut: StatutMessageCS; resultat?: string }> => {
  const message = await getMessageCSById(coproprieteId, messageId);
  if (!message || !message.validationCS) {
    throw new Error('Message non trouvé ou pas en validation');
  }

  // Vérifier que le membre n'a pas déjà voté
  const dejaVote = message.validationCS.votes.some(
    (v) => v.membreId === vote.membreId
  );
  if (dejaVote) {
    throw new Error('Ce membre a déjà voté');
  }

  // Construire le vote proprement sans undefined
  const voteNettoye: VoteMembreCS = {
    membreId: vote.membreId,
    membreNom: vote.membreNom,
    vote: vote.vote,
    dateVote: vote.dateVote,
  };
  
  // Ajouter commentaire seulement s'il existe et n'est pas vide
  if (vote.commentaire && vote.commentaire.trim() !== '') {
    voteNettoye.commentaire = vote.commentaire;
  }

  // Ajouter le vote
  const nouveauxVotes = [...message.validationCS.votes, voteNettoye];
  const votesRequis = message.validationCS.votesRequis;

  // Calculer le résultat
  const votesContre = nouveauxVotes.filter((v) => v.vote === 'contre').length;
  const totalVotes = nouveauxVotes.length;

  let nouveauStatut: StatutMessageCS = 'en_validation';
  let resultat: string | undefined;

  // Tous ont voté ?
  if (totalVotes >= votesRequis) {
    if (votesContre > 0) {
      // Au moins un contre → refusé
      nouveauStatut = 'refuse';
      resultat = votesContre === votesRequis ? 'unanimite_contre' : 'majorite_contre';
    } else {
      // Unanimité pour
      nouveauStatut = 'valide';
      resultat = 'unanimite_pour';
    }
  }

  const docRef = getDocRef(coproprieteId, messageId);

  // Mise à jour selon l'état du vote
  if (nouveauStatut === 'en_validation') {
    // Vote en cours, pas encore terminé
    await updateDoc(docRef, {
      'validationCS.votes': nouveauxVotes,
      updatedAt: Timestamp.now(),
    });
  } else {
    // Vote terminé, mise à jour complète
    await updateDoc(docRef, {
      'validationCS.votes': nouveauxVotes,
      'validationCS.dateCloture': Timestamp.now(),
      'validationCS.resultat': resultat,
      statut: nouveauStatut,
      updatedAt: Timestamp.now(),
    });
  }

  return { nouveauStatut, resultat };
};

/**
 * Récupérer les messages en attente de vote avec détails
 */
export const getMessagesEnValidationDetails = async (
  coproprieteId: string,
  membresCS: { id: string; nom: string; email: string; telephone?: string }[]
): Promise<MessageCSEnValidation[]> => {
  const messages = await getMessagesByStatut(coproprieteId, 'en_validation');
  const now = new Date();

  return messages.map((message) => {
    const validation = message.validationCS!;
    const membresAyantVote = validation.votes;
    const idsAyantVote = membresAyantVote.map((v) => v.membreId);

    // Membres n'ayant pas voté
    const membresEnAttente = membresCS
      .filter((m) => !idsAyantVote.includes(m.id))
      .map((m) => {
        const dateSoumission = validation.dateSoumission.toDate();
        const joursAttente = Math.floor(
          (now.getTime() - dateSoumission.getTime()) / (1000 * 60 * 60 * 24)
        );

        // Dernière relance
        const relances = validation.relancesManuelles?.filter(
          (r) => r.membreRelanceId === m.id
        );
        const derniereRelance = relances?.length
          ? relances[relances.length - 1].date
          : undefined;

        return {
          membreId: m.id,
          membreNom: m.nom,
          email: m.email,
          telephone: m.telephone,
          joursAttente,
          derniereRelance,
        };
      });

    // Calcul si relance requise (> 72h sans réponse et pas de relance récente)
    const heuresDepuisSoumission =
      (now.getTime() - validation.dateSoumission.toDate().getTime()) /
      (1000 * 60 * 60);
    const relanceRequise =
      heuresDepuisSoumission > DELAIS_RAPPELS.DEUXIEME_RAPPEL &&
      membresEnAttente.length > 0;

    return {
      ...message,
      membresEnAttente,
      membresAyantVote,
      progressionVote: `${membresAyantVote.length}/${validation.votesRequis}`,
      relanceRequise,
    };
  });
};

// ============================================================================
// GESTION DES RELANCES
// ============================================================================

/**
 * Enregistrer une relance manuelle
 */
export const enregistrerRelanceManuelle = async (
  coproprieteId: string,
  messageId: string,
  relance: Omit<RelanceManuelleCS, 'date'>
): Promise<void> => {
  const message = await getMessageCSById(coproprieteId, messageId);
  if (!message || !message.validationCS) {
    throw new Error('Message non trouvé ou pas en validation');
  }

  const nouvelleRelance: RelanceManuelleCS = {
    ...relance,
    date: Timestamp.now(),
  };

  const relancesExistantes = message.validationCS.relancesManuelles || [];

  const docRef = getDocRef(coproprieteId, messageId);
  await updateDoc(docRef, {
    'validationCS.relancesManuelles': [...relancesExistantes, nouvelleRelance],
    updatedAt: Timestamp.now(),
  });
};

/**
 * Enregistrer un rappel automatique envoyé
 */
export const enregistrerRappelAuto = async (
  coproprieteId: string,
  messageId: string,
  membreId: string,
  typeRappel: 'rappel24h' | 'rappel72h'
): Promise<void> => {
  const docRef = getDocRef(coproprieteId, messageId);

  await updateDoc(docRef, {
    [`validationCS.rappelsAuto.${membreId}.${typeRappel}`]: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
};

// ============================================================================
// COMPTEURS POUR BADGES / ALERTES
// ============================================================================

/**
 * Compter les messages nécessitant une relance (> 72h sans vote complet)
 */
export const compterMessagesRelanceRequise = async (
  coproprieteId: string
): Promise<number> => {
  const messages = await getMessagesByStatut(coproprieteId, 'en_validation');
  const now = new Date();
  const seuilHeures = DELAIS_RAPPELS.DEUXIEME_RAPPEL;

  return messages.filter((message) => {
    if (!message.validationCS) return false;

    const heuresDepuisSoumission =
      (now.getTime() - message.validationCS.dateSoumission.toDate().getTime()) /
      (1000 * 60 * 60);

    // Vérifier si tous ont voté
    const tousOntVote =
      message.validationCS.votes.length >= message.validationCS.votesRequis;

    return heuresDepuisSoumission > seuilHeures && !tousOntVote;
  }).length;
};

/**
 * Compter les messages en cours (brouillons + en_validation)
 */
export const compterMessagesEnCours = async (
  coproprieteId: string
): Promise<{ brouillons: number; enValidation: number }> => {
  const messages = await getMessagesEnCours(coproprieteId);

  return {
    brouillons: messages.filter((m) => m.statut === 'brouillon').length,
    enValidation: messages.filter((m) => m.statut === 'en_validation').length,
  };
};

// ============================================================================
// WORKFLOW SENTINELLE
// ============================================================================

/**
 * Marquer un message comme envoyé (après envoi Gmail réussi)
 */
export const marquerCommeEnvoye = async (
  coproprieteId: string,
  messageId: string,
  gmailMessageId: string,
  gmailThreadId?: string
): Promise<void> => {
  const docRef = getDocRef(coproprieteId, messageId);

  if (gmailThreadId) {
    await updateDoc(docRef, {
      statut: 'envoye',
      gmailMessageId,
      gmailThreadId,
      dateEnvoi: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } else {
    await updateDoc(docRef, {
      statut: 'envoye',
      gmailMessageId,
      dateEnvoi: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }
};

/**
 * Appliquer la reformulation SENTINELLE
 */
export const appliquerReformulation = async (
  coproprieteId: string,
  messageId: string,
  nouvelObjet: string,
  nouveauContenu: string
): Promise<void> => {
  const docRef = getDocRef(coproprieteId, messageId);

  await updateDoc(docRef, {
    objet: nouvelObjet,
    contenu: nouveauContenu,
    'analyseSentinelle.reformulationAppliquee': true,
    updatedAt: Timestamp.now(),
  });
};

/**
 * Lier un message à un courrier formel (transformation)
 */
export const lierACourrier = async (
  coproprieteId: string,
  messageId: string,
  courrierId: string
): Promise<void> => {
  const docRef = getDocRef(coproprieteId, messageId);

  await updateDoc(docRef, {
    courrierLieId: courrierId,
    updatedAt: Timestamp.now(),
  });
};

/**
 * Enregistrer une contestation a posteriori
 */
export const enregistrerContestation = async (
  coproprieteId: string,
  messageId: string,
  contestation: {
    parUserId: string;
    parUserNom: string;
    motif: string;
  }
): Promise<void> => {
  const message = await getMessageCSById(coproprieteId, messageId);
  if (!message) {
    throw new Error('Message non trouvé');
  }

  const nouvelleContestation = {
    ...contestation,
    date: Timestamp.now(),
  };

  const contestationsExistantes = message.contestations || [];

  const docRef = getDocRef(coproprieteId, messageId);
  await updateDoc(docRef, {
    contestations: [...contestationsExistantes, nouvelleContestation],
    updatedAt: Timestamp.now(),
  });
};

// ============================================================================
// EXPIRATION DES VOTES
// ============================================================================

/**
 * Marquer les messages expirés (appelé par Cloud Function cron)
 * Expire après 7 jours sans vote complet
 */
export const expirerMessagesEnValidation = async (
  coproprieteId: string,
  delaiExpirationJours: number = 7
): Promise<number> => {
  const messages = await getMessagesByStatut(coproprieteId, 'en_validation');
  const now = new Date();
  const batch = writeBatch(db);
  let count = 0;

  for (const message of messages) {
    if (!message.validationCS) continue;

    const joursDepuisSoumission =
      (now.getTime() - message.validationCS.dateSoumission.toDate().getTime()) /
      (1000 * 60 * 60 * 24);

    if (joursDepuisSoumission > delaiExpirationJours) {
      const docRef = getDocRef(coproprieteId, message.id);
      batch.update(docRef, {
        statut: 'expire',
        'validationCS.dateCloture': Timestamp.now(),
        'validationCS.resultat': 'expire',
        updatedAt: Timestamp.now(),
      });
      count++;
    }
  }

  if (count > 0) {
    await batch.commit();
  }

  return count;
};
